import { ContextualMenuButton } from 'app/design/icons/contextual-menu-button';
import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import { Column, Row } from 'app/design/primitives/box';
import { Button, PressableType } from 'app/design/primitives/button';
import { Modal } from 'app/design/primitives/modal';
import { Accessory as AccessoryPrimitive } from 'app/design/primitives/page/accessory';
import { Text } from 'app/design/primitives/text';
import { useNavigation } from 'app/hooks/navigation';
import { useRejectOneLoan } from 'app/network/loan';
import type { Loan } from 'app/types/loan';
import {
  canUserCompleteLoan,
  canUserRateLoan,
  getCurrentUserLoanRating,
  hasCurrentUserRatedLoan,
  LoanETA,
  LoanStatus,
} from 'app/types/loan';
import type { User } from 'app/types/user';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, View } from 'react-native';
import { getLoanInfos } from '../functions';

import { IconCalendar, IconChevronRight, IconCircleX, IconPlus } from '@tabler/icons-react-native';
import {
  CreateOfferFromRequestModal,
  useCreateOfferFromRequestModal,
} from 'app/components/create-offer-from-request-modal';
import { LoanReturnConfirmation, useLoanReturnConfirmation } from 'app/components/loan-return-confirmation';
import { addLoanToCalendar } from 'app/lib/calendar';
import { isDonationListing } from 'app/lib/listing';
import { navigateToListing } from 'app/screens/billboard/navigator-helper';
import { ModalCalendarSetting } from 'app/screens/profile/my-preferences/calendar/modal';
import { createTheme as createDSTheme } from 'app/shopify';
import { ListingSubtype, TransactionMode } from 'app/types/listing';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { isMobileNative } from 'app/utils/web-helpers';
import Toast from 'react-native-toast-message';
import type { TFunction } from 'i18next';
const theme = createDSTheme();

interface AccessorySectionProps {
  loan: Loan;
  user: User;
}

function getLoanRatingSubject(loan: Loan, isLender: boolean, fallbackName: string) {
  const otherUser = isLender ? loan.borrower : loan.lender;
  const otherUserName = [otherUser.profile?.firstName, otherUser.profile?.lastName].filter(Boolean).join(' ').trim();
  const name = otherUserName || fallbackName;

  return {
    otherUser,
    otherUserName: name,
    subjectImage: isLender
      ? (otherUser.profile?.picture ?? null)
      : (loan.listing.picture ?? loan.listing.pictures?.[0]?.url ?? null),
    subjectName: isLender ? name : loan.listing.item.name,
  };
}

function RatingThumbButtons({ onRate }: { onRate: (initialExperience: 'positive' | 'negative') => void }) {
  return (
    <Row gap="spacing.general.x2" style={{ width: '100%' }}>
      <Pressable
        onPress={() => onRate('positive')}
        style={{
          alignItems: 'center',
          borderColor: '#DDDACF',
          borderRadius: 39,
          borderWidth: 1,
          flex: 1,
          height: 48,
          justifyContent: 'center',
          paddingHorizontal: 14,
        }}
      >
        <Text fontSize={24} fontWeight="700">
          👍
        </Text>
      </Pressable>
      <Pressable
        onPress={() => onRate('negative')}
        style={{
          alignItems: 'center',
          borderColor: '#DDDACF',
          borderRadius: 39,
          borderWidth: 1,
          flex: 1,
          height: 48,
          justifyContent: 'center',
          paddingHorizontal: 14,
        }}
      >
        <Text fontSize={24} fontWeight="700">
          👎
        </Text>
      </Pressable>
    </Row>
  );
}

function RatingPromptBottomSheet({
  isOpen,
  name,
  onClose,
  onRate,
}: {
  isOpen: boolean;
  name: string;
  onClose: () => void;
  onRate: (initialExperience: 'positive' | 'negative') => void;
}) {
  const { t } = useTranslation('one-loan');

  return (
    <BottomSheet onClose={onClose} open={isOpen}>
      <Column
        alignItems="center"
        gap="spacing.general.x4"
        paddingHorizontal="spacing.general.x4"
        style={{ paddingBottom: 40, paddingTop: 24 }}
      >
        <Text fontSize={20} fontWeight="700" lineHeight={22} textAlign="center" color="black">
          {t('ratingPrompt.title', { name })}
        </Text>
        <Text color="text.secondary" fontSize={14} textAlign="center">
          {t('ratingPrompt.subtitle')}
        </Text>
        <RatingThumbButtons onRate={onRate} />
        <Pressable onPress={onClose}>
          <Text
            fontSize={14}
            fontWeight="700"
            lineHeight={16}
            textAlign="center"
            style={{ color: '#1F2635', textDecorationLine: 'underline' }}
          >
            {t('ratingPrompt.dismiss')}
          </Text>
        </Pressable>
      </Column>
    </BottomSheet>
  );
}

function RatingAccessory({
  hasRated,
  isRecommended,
  name,
  onPress,
}: {
  hasRated: boolean;
  isRecommended: boolean;
  name: string;
  onPress: () => void;
}) {
  const { t } = useTranslation('one-loan');

  return (
    <AccessoryPrimitive>
      <View
        style={{
          marginHorizontal: theme.spacing['spacing.general.x4'],
          marginTop: theme.spacing['spacing.general.x4'],
        }}
      >
        <Pressable
          onPress={onPress}
          style={
            hasRated
              ? {
                  alignItems: 'center',
                  borderColor: theme.colors['surface.disabled'],
                  borderRadius: 24,
                  borderWidth: 1,
                  flexDirection: 'row',
                  gap: 8,
                  justifyContent: 'center',
                  padding: 16,
                }
              : {
                  alignItems: 'center',
                  backgroundColor: '#1F2635',
                  borderRadius: 1000,
                  justifyContent: 'center',
                  padding: 16,
                }
          }
        >
          {hasRated ? (
            <>
              <Text fontSize={14} lineHeight={13.5} color="black" paddingLeft="spacing.general.x2">
                {t('ratingAccessory.mine')}
              </Text>
              <Text fontSize={14} lineHeight={13.5} color="black">
                {isRecommended ? '👍 ' : '👎 '}
                <Text
                  fontSize={14}
                  fontWeight="700"
                  lineHeight={13.5}
                  style={{
                    color: isRecommended ? theme.colors['text.emphasized'] : theme.colors['text.error'],
                  }}
                >
                  {t(isRecommended ? 'ratingAccessory.recommended' : 'ratingAccessory.notRecommended')}
                </Text>
              </Text>
              <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} size={24} color="#1F2635" />
            </>
          ) : (
            <Text color="surface.default" fontSize={16} fontWeight="500" lineHeight={18} textAlign="center">
              {t('ratingAccessory.rate', { name })}
            </Text>
          )}
        </Pressable>
      </View>
    </AccessoryPrimitive>
  );
}

function getTranslationSuffix({
  isDonation,
  isSell,
  isService,
  loanType,
}: {
  isDonation: boolean;
  isSell: boolean;
  isService: boolean;
  loanType: string;
}) {
  if (isSell) return 'sale';
  if (isService) return 'service';
  if (isDonation) return 'donation';
  return loanType;
}

function getCtaButtonText({
  ctaConfirmReturn,
  isDonation,
  isLender,
  isParticipations,
  isSell,
  isService,
  loan,
  t,
  tSuffix,
}: {
  ctaConfirmReturn: boolean;
  isDonation: boolean;
  isLender: boolean;
  isParticipations: boolean;
  isSell: boolean;
  isService: boolean;
  loan: Loan;
  t: TFunction<'one-loan'>;
  tSuffix: string;
}) {
  if (loan.status === LoanStatus.Rejected) return undefined;
  if (isParticipations) {
    return isLender ? t('viewParticipationButtonText', { ns: 'one-listing' }) : t('listParticipants');
  }
  if (ctaConfirmReturn) {
    const tKey = isSell
      ? 'sellDoneButtonText'
      : isService
        ? 'serviceDoneButtonText'
        : isDonation
          ? 'donationDoneButtonText'
          : 'itemReturnedButtonText';
    return t(tKey, { ns: 'one-listing' });
  }
  if (loan.status !== LoanStatus.Completed && loan.status !== LoanStatus.Returned) {
    return t(`changeDatesButtonText.${tSuffix}`);
  }
  return undefined;
}

function getContextualOptions({
  addToCalendar,
  ctaConfirmReturn,
  isLender,
  loanStatus,
  openCancelConfirmation,
  showDatesPicker,
  t,
  tSuffix,
}: {
  addToCalendar: () => void;
  ctaConfirmReturn: boolean;
  isLender: boolean;
  loanStatus?: LoanETA;
  openCancelConfirmation: () => void;
  showDatesPicker: () => void;
  t: TFunction<'one-loan'>;
  tSuffix: string;
}) {
  const options: { icon: JSX.Element; buttonText: string; onPress: () => void }[] = [];

  if (
    isMobileNative &&
    loanStatus !== LoanETA.pending &&
    loanStatus !== LoanETA.past &&
    loanStatus !== LoanETA.returned
  ) {
    options.push({
      icon: <IconPlus strokeWidth={ICON_STROKE_WIDTH} />,
      buttonText: t('addToCalendarButtonText'),
      onPress: addToCalendar,
    });
  }

  if (ctaConfirmReturn && loanStatus !== LoanETA.returned) {
    options.push({
      icon: <IconCalendar strokeWidth={ICON_STROKE_WIDTH} />,
      buttonText: t(`changeDatesButtonText.${tSuffix}`),
      onPress: showDatesPicker,
    });
  }

  if (isLender || loanStatus === LoanETA.scheduled || loanStatus === LoanETA.pending) {
    options.push({
      icon: <IconCircleX strokeWidth={ICON_STROKE_WIDTH} />,
      buttonText: t(`cancelLoanButtonText.${tSuffix}`),
      onPress: openCancelConfirmation,
    });
  }

  return options;
}

export function Accessory({ loan: originalLoan, user }: AccessorySectionProps) {
  const { t } = useTranslation('one-loan');
  const { t: tOneChannel } = useTranslation('one-channel');

  const navigation = useNavigation();
  const cancelLoan = useRejectOneLoan();
  const [isCancelConfirmationModalOpen, setIsCancelConfirmationModalOpen] = useState(false);
  const [isContextMenuVisible, setIsContextMenuVisible] = useState(false);
  const [isRatingPromptOpen, setIsRatingPromptOpen] = useState(false);

  const createOfferFromRequestModalProps = useCreateOfferFromRequestModal(originalLoan);
  const { setIsOpen: setIsCreateOfferModalOpen } = createOfferFromRequestModalProps;

  const loanReturnConfirmationProps = useLoanReturnConfirmation(
    originalLoan,
    () => setIsCreateOfferModalOpen(true),
    (completedLoan) => {
      if (completedLoan.status === LoanStatus.Completed && !hasCurrentUserRatedLoan(completedLoan, user.id)) {
        setIsRatingPromptOpen(true);
      }
    },
  );
  const { loan, setIsOpen: setIsReturnConfirmationModalOpen } = loanReturnConfirmationProps;

  const { loanType, ctaConfirmReturn, isLender, loanStatus } = (() => {
    const infos = getLoanInfos(loan, user);
    return {
      ...infos,
      ctaConfirmReturn: canUserCompleteLoan(loan, infos.isLender),
    };
  })();

  const isService = loan.listing.subtype === ListingSubtype.service;
  const isDonation = isDonationListing(loan.listing);
  const isSell = loan.listing.transactionMode === TransactionMode.sell;
  const isParticipations = Boolean(loan.listing.maxParticipants && loan.listing.maxParticipants > 0);
  const currentUserRating = getCurrentUserLoanRating(loan, user.id);
  const hasRated = typeof currentUserRating === 'number';
  const canRate = canUserRateLoan(loan, user.id);
  const isRecommended = typeof currentUserRating === 'number' && currentUserRating >= 3;
  const ratingSubject = getLoanRatingSubject(loan, isLender, t('deletedUser', { ns: 'common' }));

  const tSuffix = getTranslationSuffix({ isDonation, isSell, isService, loanType });

  const modalCalendarSettingState = useState(false);
  const addToCalendar = async () => {
    const res = await addLoanToCalendar(tOneChannel, user.id, loan);
    if (res === 'permission-denied') {
      modalCalendarSettingState[1](true);
    } else if (res === 'error') {
      Toast.show({
        type: 'error',
        text1: tOneChannel('genericError', { ns: 'common' }),
        position: 'bottom',
        bottomOffset: 100,
        visibilityTime: 3000,
      });
    }
  };

  const onCancelLoanPress = () => {
    cancelLoan.mutate(loan.id, {
      onSuccess: () => {
        navigation.pop();
      },
    });
  };

  const showDatesPicker = () => {
    navigation.navigate(
      'loan-dates-picker',
      {
        loanId: loan.id,
        url: loan.channelURL,
        promptIntroduceYourself: false,
        goBackTo: 'one-loan',
      },
      { pop: true },
    );
  };

  const ctaButtonText = getCtaButtonText({
    ctaConfirmReturn,
    isDonation,
    isLender,
    isParticipations,
    isSell,
    isService,
    loan,
    t,
    tSuffix,
  });
  const ctaOnPress = () =>
    isParticipations
      ? isLender
        ? navigateToListing(navigation, loan.listing.id)
        : navigation.navigate('loan-participants', { loanId: loan.id }, { pop: true })
      : ctaConfirmReturn
        ? setIsReturnConfirmationModalOpen(true)
        : showDatesPicker();

  const openRating = (initialExperience?: 'positive' | 'negative') => {
    setIsRatingPromptOpen(false);
    navigation.navigate(
      'new-rating',
      {
        loanId: loan.id,
        listingId: loan.listingId,
        channelUrl: loan.channelURL,
        isLender,
        subjectImage: ratingSubject.subjectImage,
        subjectName: ratingSubject.subjectName,
        initialExperience,
      },
      { pop: true },
    );
  };

  const openMyRating = () => {
    navigation.navigate(
      'ratings',
      {
        userId: ratingSubject.otherUser.id,
        subjectName: ratingSubject.otherUserName,
        scrollToLoanId: loan.id,
      },
      { pop: true },
    );
  };

  const contextualOptions = getContextualOptions({
    addToCalendar: () => void addToCalendar(),
    ctaConfirmReturn,
    isLender,
    loanStatus,
    openCancelConfirmation: () => setIsCancelConfirmationModalOpen(true),
    showDatesPicker,
    t,
    tSuffix,
  });

  return (
    <>
      {ctaButtonText ? (
        <AccessoryPrimitive>
          <Row marginHorizontal="spacing.general.x4" marginTop="spacing.general.x4">
            <Button flex={1} onPress={ctaOnPress} variant="primary">
              <Text color="surface.default" fontWeight="700" textAlign="center">
                {ctaButtonText}
              </Text>
            </Button>
            {contextualOptions.length > 0 && (
              <Button
                onPress={() => {
                  setIsContextMenuVisible(true);
                }}
              >
                <View style={{ top: 0, left: 11, position: 'relative' }}>
                  <ContextualMenuButton />
                </View>
              </Button>
            )}
          </Row>
        </AccessoryPrimitive>
      ) : hasRated || canRate ? (
        <RatingAccessory
          hasRated={hasRated}
          isRecommended={isRecommended}
          name={ratingSubject.otherUser.profile?.firstName || ratingSubject.otherUserName}
          onPress={hasRated ? openMyRating : () => openRating()}
        />
      ) : null}

      <LoanReturnConfirmation {...loanReturnConfirmationProps} />
      <RatingPromptBottomSheet
        isOpen={isRatingPromptOpen}
        name={ratingSubject.otherUser.profile?.firstName || ratingSubject.otherUserName}
        onClose={() => setIsRatingPromptOpen(false)}
        onRate={openRating}
      />

      <CreateOfferFromRequestModal {...createOfferFromRequestModalProps} />

      <Modal onClose={() => setIsCancelConfirmationModalOpen(false)} open={isCancelConfirmationModalOpen} transparent>
        <Modal.Card>
          <Modal.Title>{t(`cancelLoanModal.title.${tSuffix}`)}</Modal.Title>

          <Column>
            <Text textAlign="center">{t(`cancelLoanModal.text.${tSuffix}`)}</Text>
          </Column>

          <View>
            <Button
              loading={cancelLoan.isPending}
              marginBottom="spacing.general.x4"
              onPress={onCancelLoanPress}
              variant="primary"
            >
              {t(`cancelLoanModal.primaryButtonText.${tSuffix}`)}
            </Button>

            <Button onPress={() => setIsCancelConfirmationModalOpen(false)} variant="secondary">
              {t(`cancelLoanModal.secondaryButtonText.${loanType}`)}
            </Button>
          </View>
        </Modal.Card>
      </Modal>

      <BottomSheet onClose={() => setIsContextMenuVisible(false)} open={isContextMenuVisible}>
        <Column gap="spacing.general.x1" paddingTop="spacing.general.x1">
          {contextualOptions.map((option) => (
            <View
              key={option.buttonText}
              style={{
                borderBottomWidth: 1,
                borderBottomColor: theme.colors['surface.tertiary-neutral'],
              }}
            >
              <Button
                marginHorizontal="spacing.general.x4"
                onPress={() => {
                  setIsContextMenuVisible(false);
                  // @TODO: How can we stack both animations, without using a dumb setTimeout() ..?
                  // (Trying to execute option.onPress directly here doesn't work. Pretty sure it's because of the modal dismissal animation.)
                  setTimeout(option.onPress, 400);
                }}
                pressableType={PressableType.GestureHandler}
              >
                <Row alignItems="center" gap="spacing.general.x4">
                  <View>{option.icon}</View>
                  <Text flex={1} style={{ fontSize: 16 }} variant="bodyBase">
                    {option.buttonText}
                  </Text>
                </Row>
              </Button>
            </View>
          ))}
        </Column>
      </BottomSheet>

      <ModalCalendarSetting modalState={modalCalendarSettingState} />
    </>
  );
}
